import { NgClass, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { UserGroupService } from '@core/services/user-group.service';
import { environment } from '@environments/environment';
import { AppNavigationCreator } from '@layout/core/classes';
import { AppNavigation } from '@layout/core/interfaces';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipFocusHandlerDirective } from '@shared/directives/tooltip-focus-handler.directive';
import { AppRoutes } from '@shared/helpers';
import { ButtonModule } from 'primeng/button';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-navigation[isSidebarCollapsed]',
    templateUrl: './app-navigation.component.html',
    styleUrls: ['./app-navigation.component.scss'],
    standalone: true,
    imports: [
        TooltipModule,
        RouterLink,
        RouterLinkActive,
        NgTemplateOutlet,
        NgClass,
        ButtonModule,
        OverlayPanelModule,
        TranslateModule,
        TooltipFocusHandlerDirective,
        NgOptimizedImage,
    ],
})
export class AppNavigationComponent implements OnChanges {
    @Input() isSidebarCollapsed = true;
    @Input() isOnMobile = false;
    @Output() toggleIsCollapsed: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('ovMyOrders') ovMyOrders: OverlayPanel | null = null;
    ovMyOrdersOpened = false;

    appNavigation: AppNavigation[][] = [];
    activeMenus: boolean[][] = [];
    overlayStyle = { 'margin-left': '90px', 'margin-top': '-90px' };

    isOnSequencingUrl: boolean = false;

    myOrdersSubmenu: AppNavigation[] = [];
    readonly transnetyxServicesSubmenu: AppNavigation[] = [
        AppNavigationCreator.create({
            id: 'automated-genotyping',
            label: 'Automated Genotyping',
            url: environment.automatedGenotypingUrl,
            icon: 'assets/images/app-icons/automated-genotyping-icon.svg',
            internalRoute: false,
            isAsset: true,
        }),
        AppNavigationCreator.create({
            id: 'genetic-monitoring',
            label: 'Genetic Monitoring',
            url: environment.geneticMonitoringUrl,
            icon: 'assets/images/app-icons/genetic-monitoring-icon.svg',
            internalRoute: false,
            isAsset: true,
        }),
        AppNavigationCreator.create({
            id: 'microbiome',
            label: 'Microbiome',
            url: environment.microbiomeUrl,
            icon: 'assets/images/app-icons/microbiome-icon.svg',
            internalRoute: false,
            isAsset: true,
        }),
        AppNavigationCreator.create({
            id: 'colony-and-ami',
            label: 'Colony + AMI',
            url: environment.colonyUrl,
            icon: 'assets/images/app-icons/colony-icon.svg',
            internalRoute: false,
            isAsset: true,
        }),
        AppNavigationCreator.create({
            id: 'tissue',
            label: 'Tissue',
            url: environment.tissueUrl,
            icon: 'assets/images/app-icons/tissue-icon.svg',
            internalRoute: false,
            isAsset: true,
        }),
        AppNavigationCreator.create({
            id: 'tag-center',
            label: 'TAGCenter',
            url: 'https://tagcenter.info/',
            icon: 'assets/images/app-icons/tag-center-icon.svg',
            internalRoute: false,
            isAsset: true,
        }),
    ];

    readonly helpAndSupportSubmenu: AppNavigation[] = [
        AppNavigationCreator.create({
            id: 'support-hub',
            label: 'Support Hub',
            url: 'https://www.transnetyx.com/support',
            icon: 'fa-light fa-circle-question',
            internalRoute: false,
        }),
        AppNavigationCreator.create({
            id: 'support-phone',
            label: '1-888-321-2113',
            url: 'tel:18883212113',
            icon: 'fa-light fa-phone',
            internalRoute: false,
        }),
        AppNavigationCreator.create({
            id: 'support-email',
            label: 'Help@transnetyx.com',
            url: 'mailto:help@transnetyx.com',
            icon: 'fa-light fa-envelope',
            internalRoute: false,
        }),
        AppNavigationCreator.create({
            id: 'live-chat',
            label: 'Live Chat',
            url: '',
            icon: 'fa-light fa-comments',
            internalRoute: false,
            action: this.openChat,
        }),
        AppNavigationCreator.create({
            id: 'terms-of-use',
            label: 'Terms of Use',
            url: 'https://info.transnetyx.com/hubfs/Transnetyx%20Terms%20of%20Use.pdf',
            icon: 'fa-light fa-file-contract',
            internalRoute: false,
        }),
    ];
    routerUrl: string = this.router.url;

    constructor(
        private router: Router,
        private userGroupService: UserGroupService
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.routerUrl = event.urlAfterRedirects;
                this.computeActiveMenus();
            }
        });

        this.userGroupService.currentGroup$.subscribe(() => {
            this.initAppNavigation();
        });
    }

    ngOnChanges(): void {
        this.initAppNavigation();

        if (this.isSidebarCollapsed) {
            this.appNavigation.forEach(navList => {
                navList.forEach(navItem => {
                    navItem.expanded = false;
                });
            });
        }
    }

    navItemClick(navItem: AppNavigation): void {
        if (!navItem.submenu) {
            return;
        }

        navItem.expanded = !navItem.expanded;

        this.hideOverlays();

        if (navItem.expanded) {
            this.appNavigation.forEach(navList => {
                navList.forEach(item => {
                    if (item.expanded && item.id !== navItem.id) {
                        item.expanded = false;
                    }
                });
            });
        }

        if (this.isSidebarCollapsed) {
            this.toggleIsCollapsed.emit();
        }
    }

    navItemHover(navItemId: string, event: any): void {
        if (!this.isSidebarCollapsed) {
            return;
        } else if (navItemId === 'my-orders') {
            this.ovMyOrders?.show(event);
            this.ovMyOrdersOpened = true;
        } else {
            this.hideOverlays();
        }
    }

    hideOverlays(): void {
        this.ovMyOrders?.hide();
        this.ovMyOrdersOpened = false;
    }

    isRichMenuActive(listIndex: number, navIndex: number, _navigationSize: number): boolean {
        const navItem = this.appNavigation[listIndex][navIndex];

        return (navItem.submenu || []).some(el => {
            return el.url != '' && this.routerUrl.includes(el.url);
        });
    }

    openChat(): void {
        // @ts-ignore
        window['happyFoxChat'].showWidget();
        // @ts-ignore
        window['happyFoxChat'].expandChatbox();
    }

    computeActiveMenus() {
        if (this.appNavigation && this.appNavigation.length > 0) {
            this.activeMenus = this.appNavigation.map((nav, listIndex) => {
                return nav.map((_, navIndex) =>
                    this.isRichMenuActive(listIndex, navIndex, this.appNavigation[listIndex].length)
                );
            });
        }
    }

    private initAppNavigation(): void {
        this.appNavigation = [];
        this.initMyOrdersSubmenu();

        if (this.isOnMobile) {
            this.appNavigation.push([...this.initNavTwo(), ...this.initNavOne()]);
        } else {
            this.appNavigation.push(this.initNavOne());
        }

        this.computeActiveMenus();
    }

    private initNavTwo(): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'transnetyx-services',
                label: 'appNav.transnetyxServices',
                url: '',
                icon: '',
                expanded: false,
                submenu: this.transnetyxServicesSubmenu,
                internalRoute: false,
                isAsset: true,
            }),
            AppNavigationCreator.create({
                id: 'help-and-support',
                label: 'profileMenu.supportPanelHeader',
                url: '',
                icon: 'fa-light fa-circle-question',
                expanded: false,
                submenu: this.helpAndSupportSubmenu,
                internalRoute: false,
            }),
        ];
    }

    private initNavOne(): AppNavigation[] {
        return [
            AppNavigationCreator.create({
                id: 'dashboard',
                label: 'appNav.dashboard',
                url: AppRoutes.routes().DASHBOARD,
                icon: 'fa-light fa-objects-column',
                internalRoute: true,
            }),
            AppNavigationCreator.create({
                id: 'my-orders',
                label: 'appNav.myOrders',
                url: '',
                icon: 'fa-light fa-basket-shopping',
                expanded: false,
                submenu: this.myOrdersSubmenu,
                internalRoute: true,
            }),
            AppNavigationCreator.create({
                id: 'inventory',
                label: 'appNav.inventory',
                url: AppRoutes.routes().INVENTORY_BASE_URL,
                icon: 'fa-light fa-square-list',
                internalRoute: true,
            }),
        ];
    }

    private initMyOrdersSubmenu() {
        this.myOrdersSubmenu = [
            AppNavigationCreator.create({
                id: 'orders-and-results',
                label: 'appNav.ordersAndResults',
                url: AppRoutes.routes().ORDERS_LIST,
                icon: '',
                internalRoute: true,
            }),
            AppNavigationCreator.create({
                id: 'new-order',
                label: 'appNav.newOrder',
                url: AppRoutes.routes().ORDERS_SETUP,
                icon: '',
                internalRoute: true,
            }),
            AppNavigationCreator.create({
                id: 'new-order',
                label: 'appNav.newOrder',
                url: AppRoutes.routes().ORDERS_ABSTRACT_BASE_URL,
                icon: '',
                internalRoute: true,
                isHidden: true,
            }),
            AppNavigationCreator.create({
                id: 'checkMate-retest',
                label: 'appNav.checkMateRetest',
                url: AppRoutes.routes().ORDERS_CHECKMATE_RETEST_LIST,
                icon: '',
                internalRoute: true,
            }),
            AppNavigationCreator.create({
                id: 'checkMate-retest-setup',
                label: 'appNav.checkMateRetestSetup',
                url: AppRoutes.routes().ORDERS_CHECKMATE_RETEST_SETUP,
                icon: '',
                internalRoute: true,
                isHidden: true,
            }),
        ];
    }
}
